import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useWebTranslation } from "../../../../translations";
import { FlexCol } from "../../../../ui-kit/Layout/Flex";
import { FormField } from "../../../../components-shared/Form";
import { Typography } from "../../../../ui-kit/Typography";
import CurrencyInput from "../../../CurrencyInput/CurrencyInput";
import Gutter from "../../../../ui-kit/Gutter";
import { getCurrencySymbol, getDefaultLocale } from "../../../../utils/site";
import { formatCurrency } from "utils/utils";

type LifeInsuranceProps = {
  title: string;
  value: number;
  onChange: (amount: number) => void;
  min: number;
  max: number;
};

const LifeInsurance: FC<LifeInsuranceProps> = ({
  title,
  value,
  onChange,
  min,
  max,
}) => {
  const { t } = useWebTranslation();
  const amountInput = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState<number>(value);

  useEffect(() => {
    if (value >= min && value <= max) {
      setLocalValue(value);
    }
  }, [value, min, max]);

  useEffect(() => {
    amountInput.current?.setAttribute("min", `${min}`);
    amountInput.current?.setAttribute("max", `${max}`);
  }, [amountInput, min, max]);

  const onChangeText = (lifeLimit: string) => {
    const amount = lifeLimit ? parseInt(lifeLimit, 10) : 0;
    setLocalValue(amount);
    onChange(amount);
  };

  const error = (
    <>
      {localValue < min && (
        <Typography color="error">
          {t("choose_life_insurance.life_insurance.error_min", {
            min: formatCurrency(min, { maximumFractionDigits: 0 }),
          })}
        </Typography>
      )}
      {localValue > max && (
        <Typography color="error">
          {t("choose_life_insurance.life_insurance.error_max", {
            max: formatCurrency(max, { maximumFractionDigits: 0 }),
          })}
        </Typography>
      )}
    </>
  );

  return (
    <FlexCol>
      <Typography variant="body2">
        <strong>{title}</strong>
      </Typography>
      <Gutter size={1} />
      <FormField>
        <CurrencyInput
          nativeRef={amountInput}
          inputMode="numeric"
          placeHolder={t("choose_life_insurance.life_insurance.placeholder")}
          value={localValue === 0 ? "" : `${localValue}`}
          onChangeText={onChangeText}
          style={{
            border: 0,
            padding: 16,
          }}
          currencySymbol={getCurrencySymbol()}
          intlConfig={{
            locale: getDefaultLocale(),
          }}
        />
      </FormField>
      {error && (
        <>
          <Gutter size={1} />
          {error}
        </>
      )}
    </FlexCol>
  );
};

export default LifeInsurance;
